import { CustomerFormFields } from '@/types/customers';
import { Card, Grid, Stack, Typography } from '@mui/material';
import FormSubmissionButtons from '../FormSubmissionButtons';
import {
  SVCheckbox,
  SVCurrencyInput,
  SVDateTimePicker,
  SVEmailInput,
  SVForm,
  SVPhoneInput,
  SVAutocomplete,
  SVSSNInput,
  SVTextInput,
} from '../core';
import { useGetTeammateListForSelection } from '@/hooks/requests/useTeammates';
import AddressFormFields from '../AddressFormFields';
import dayjs from 'dayjs';
import { useGetCompanyListForSelection } from '@/hooks/requests/useCompanies';
import { FORM_FIELDS } from '@/constants/formFields';
import { useAuthUser } from '@/hooks/stores/useAuthUser';
import { TeammateRole } from '@/types/teammates';
import { initialCustomerForm } from '@/constants/customers';
import { MESSAGES } from '@/constants/messages';
import { useAddCustomer, useEditCustomer } from '@/hooks/requests/useCustomers';
import { useGetUser } from '@/hooks/requests/useUser';
import showToast from '@/utils/toast';
import { useForm } from 'react-hook-form';

type CustomersFormProps = {
  isEdit?: boolean;
  defaultValues?: CustomerFormFields;
  onLeave?: () => void;
  onSuccess: (uuid: string) => void;
};

export default function CustomersForm({
  defaultValues,
  isEdit = false,
  onLeave,
  onSuccess,
}: CustomersFormProps) {
  const { isStaff } = useAuthUser();

  const { user } = useGetUser();
  const { addCustomer, isAddCustomerPending } = useAddCustomer();
  const { editCustomer, isEditCustomerPending } = useEditCustomer();

  const formMethods = useForm<CustomerFormFields>({
    defaultValues: defaultValues
      ? defaultValues
      : {
          ...initialCustomerForm,
          company: !isStaff ? user?.teammateInfo?.company.uuid : '',
        },
    mode: 'onBlur',
  });

  async function handleSubmit(data: CustomerFormFields) {
    try {
      let response = '';
      if (isEdit) {
        await editCustomer(data);
        response = defaultValues?.uuid || '';
      } else {
        response = await addCustomer(data);
      }

      showToast({
        type: 'success',
        data: {
          content: `Customer has been successfully ${isEdit ? 'edited' : 'added'}`,
        },
      });
      onSuccess(response);
    } catch (error: any) {
      showToast({
        type: 'error',
        data: {
          content: error?.response?.data
            ? error.response.data
            : MESSAGES.genericError,
        },
      });
    }
  }

  const [company, sameMailingAddress] = formMethods.watch([
    'company',
    'sameMailingAddress',
  ]);

  const { companiesList } = useGetCompanyListForSelection({}, true);
  const { teammatesList, isTeammatesListPending } =
    useGetTeammateListForSelection(
      company
        ? {
            company_id: company,
            roles: [TeammateRole.ADMIN, TeammateRole.ADVISOR],
          }
        : { roles: [TeammateRole.ADMIN, TeammateRole.ADVISOR] },
    );

  const doesntHaveTeammates =
    Boolean(company) && teammatesList.length === 0 && !isTeammatesListPending;

  return (
    <SVForm methods={formMethods} onSubmit={handleSubmit}>
      <Stack spacing={2}>
        <Card>
          <Typography variant="h5" mb={2}>
            Basic Info
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <SVTextInput
                name="firstName"
                disabled={isEdit}
                {...FORM_FIELDS.FIRST_NAME}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <SVTextInput
                name="middleName"
                disabled={isEdit}
                {...FORM_FIELDS.MIDDLE_NAME}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <SVTextInput
                name="lastName"
                disabled={isEdit}
                {...FORM_FIELDS.LAST_NAME}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <SVPhoneInput
                name="phone"
                disabled={isEdit}
                {...FORM_FIELDS.PHONE}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <SVPhoneInput
                name="confirmPhone"
                disabled={isEdit}
                validateMatch="phone"
                {...FORM_FIELDS.CONFIRM_PHONE}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <SVDateTimePicker
                name="dob"
                disabled={isEdit}
                maxDate={dayjs(new Date()).subtract(18, 'year')}
                {...FORM_FIELDS.DOB}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <SVEmailInput
                name="email"
                disabled={isEdit}
                {...FORM_FIELDS.EMAIL}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <SVEmailInput
                name="confirmEmail"
                validateMatch="email"
                disabled={isEdit}
                {...FORM_FIELDS.CONFIRM_EMAIL}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <SVSSNInput name="ssn" disabled={isEdit} {...FORM_FIELDS.SSN} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <SVSSNInput
                name="confirmSsn"
                matchingInputName="ssn"
                disabled={isEdit}
                {...FORM_FIELDS.CONFIRM_SSN}
              />
            </Grid>
            {isStaff && (
              <Grid size={{ xs: 12, md: 6 }}>
                <SVAutocomplete
                  name="company"
                  options={companiesList}
                  disabled={isEdit}
                  {...FORM_FIELDS.COMPANY}
                />
              </Grid>
            )}
            {doesntHaveTeammates ? (
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography color="error">
                  Customers must be associated to a teammate and the current
                  selected company has no assigned teammates.
                </Typography>
              </Grid>
            ) : (
              <Grid size={{ xs: 12, md: 6 }}>
                {teammatesList.length ? (
                  <SVAutocomplete
                    name="advisors"
                    options={teammatesList}
                    multiple
                    disabled={!company}
                    {...FORM_FIELDS.ADVISORS}
                  />
                ) : null}
              </Grid>
            )}
          </Grid>
        </Card>
        <Card>
          <Typography variant="h5" mb={2}>
            Current Address
          </Typography>
          <AddressFormFields
            line1Name="currentAddress.line1"
            line2Name="currentAddress.line2"
            cityName="currentAddress.city"
            stateName="currentAddress.state"
            postalCodeName="currentAddress.postalCode"
          />
        </Card>
        <Card>
          <Typography variant="h5" mb={2}>
            Mailing Address
          </Typography>
          <SVCheckbox name="sameMailingAddress" {...FORM_FIELDS.SAME_ADDRESS} />
          {!sameMailingAddress && (
            <AddressFormFields
              line1Name="mailingAddress.line1"
              line2Name="mailingAddress.line2"
              cityName="mailingAddress.city"
              stateName="mailingAddress.state"
              postalCodeName="mailingAddress.postalCode"
            />
          )}
        </Card>
        {isStaff && (
          <Card>
            <Typography variant="h5" mb={2}>
              Customer Deposit Rules
            </Typography>
            <Grid spacing={2} container>
              <Grid size={{ xs: 12, md: 6 }}>
                <SVCurrencyInput
                  name="min"
                  required={false}
                  {...FORM_FIELDS.MINIMUM_INITIAL_DEPOSIT}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <SVCurrencyInput
                  name="max"
                  required={false}
                  {...FORM_FIELDS.MAXIMUM_INITIAL_DEPOSIT}
                />
              </Grid>
            </Grid>
          </Card>
        )}
        {doesntHaveTeammates && (
          <Typography color="error">
            Customers must be associated to a teammate and the current selected
            company has no assigned teammates.
          </Typography>
        )}
        <FormSubmissionButtons
          isLoading={isAddCustomerPending || isEditCustomerPending}
          onLeave={onLeave}
          submitButtonProps={{
            disabled: doesntHaveTeammates,
          }}
        />
      </Stack>
    </SVForm>
  );
}
